import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../api";
import { t } from "../i18n";
import { useEscapeLayer } from "../utils/escapeLayers";
import { XIcon, RotateLeftIcon, RotateRightIcon, CropIcon, BrightnessIcon } from "./Icons";

// Редактор фото в окне публикации: обрезка, поворот на 90° и яркость.
//
// Всё делается в браузере, на холсте. Исходный снимок не трогается: по
// «Готово» в форму встаёт новый JPEG на место старого (та же позиция, та же
// обложка), «Отмена» ничего не меняет.
//
// Рамка обрезки хранится в долях повёрнутого кадра (0…1), а не в пикселях
// экрана: тогда она не зависит от размера окна, и в готовый снимок она
// переносится по полному разрешению фото.

const ASPECTS = [
  { id: "free", ratio: null },
  { id: "1:1", ratio: 1 },
  { id: "4:3", ratio: 4 / 3 },
  { id: "3:4", ratio: 3 / 4 }
];
const FULL_CROP = Object.freeze({ x: 0, y: 0, w: 1, h: 1 });
// Меньше рамку не сжать — за неё уже не ухватиться пальцем.
const MIN_CROP_PX = 40;
// Предел площади холста на iPhone (~16,7 млн точек): снимок крупнее при
// сохранении уменьшается до него, иначе холст выходит пустым.
const MAX_OUTPUT_PIXELS = 16 * 1000 * 1000;
const STAGE_PADDING = 16;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.decoding = "async";
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error("image-load-failed"));
  image.src = src;
});

// Самая большая рамка нужного соотношения по центру кадра. ratio — в
// пикселях; в долях кадра оно другое, если кадр не квадратный.
const centeredCrop = (ratio, frameWidth, frameHeight) => {
  if (!ratio || !frameWidth || !frameHeight) return { ...FULL_CROP };
  const normalized = ratio * (frameHeight / frameWidth);
  let w = 1;
  let h = w / normalized;
  if (h > 1) {
    h = 1;
    w = normalized;
  }
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
};

// Рамка после поворота кадра на 90°: та же область снимка в новых долях.
const rotateCrop = (crop, clockwise) => (clockwise
  ? { x: 1 - (crop.y + crop.h), y: crop.x, w: crop.h, h: crop.w }
  : { x: crop.y, y: 1 - (crop.x + crop.w), w: crop.h, h: crop.w });

// Перетаскивание: mode — "move" или угол ("nw", "ne", "sw", "se"); dx, dy —
// сдвиг в долях кадра; ratio — соотношение в долях кадра или null.
const dragCrop = (mode, start, dx, dy, ratio, minW, minH) => {
  if (mode === "move") {
    return {
      ...start,
      x: clamp(start.x + dx, 0, 1 - start.w),
      y: clamp(start.y + dy, 0, 1 - start.h)
    };
  }
  const west = mode.includes("w");
  const north = mode.includes("n");
  // Противоположный угол стоит на месте.
  const anchorX = west ? start.x + start.w : start.x;
  const anchorY = north ? start.y + start.h : start.y;
  const edgeX = (west ? start.x : start.x + start.w) + dx;
  const edgeY = (north ? start.y : start.y + start.h) + dy;
  const maxW = west ? anchorX : 1 - anchorX;
  const maxH = north ? anchorY : 1 - anchorY;
  let w = clamp(west ? anchorX - edgeX : edgeX - anchorX, minW, maxW);
  let h = clamp(north ? anchorY - edgeY : edgeY - anchorY, minH, maxH);
  if (ratio) {
    // Рамка вписывается в прямоугольник до курсора, сохраняя соотношение.
    if (w / h > ratio) w = h * ratio;
    else h = w / ratio;
    if (w > maxW) { w = maxW; h = w / ratio; }
    if (h > maxH) { h = maxH; w = h * ratio; }
  }
  return {
    x: west ? anchorX - w : anchorX,
    y: north ? anchorY - h : anchorY,
    w,
    h
  };
};

const sameCrop = (left, right) => ["x", "y", "w", "h"].every((key) => Math.abs(left[key] - right[key]) < 0.0005);

const PhotoEditor = ({ source, fileName = "photo.jpg", onCancel, onSave }) => {
  const [status, setStatus] = useState("loading");
  const [image, setImage] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [crop, setCrop] = useState(FULL_CROP);
  const [aspect, setAspect] = useState("free");
  const [brightness, setBrightness] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const stopDragRef = useRef(null);

  useEscapeLayer(true, () => { if (!saving) onCancel(); });
  // Окно закрыли посреди перетаскивания — слушатели окна снимаются.
  useEffect(() => () => stopDragRef.current?.(), []);

  // Снимок: свой файл — сразу, снимок черновика — с сервера (адрес панели,
  // с той же авторизацией, что и остальные запросы).
  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    (async () => {
      try {
        let blob = source instanceof Blob ? source : null;
        if (!blob) {
          const url = String(source || "");
          // Картинка уже в самом адресе (data:, blob:) — серверу её не отправить.
          const response = /^(data|blob):/i.test(url)
            ? await fetch(url)
            : await apiFetch(url, { timeoutMs: 45000, retry: true });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          blob = await response.blob();
        }
        objectUrl = URL.createObjectURL(blob);
        const loaded = await loadImage(objectUrl);
        if (cancelled) return;
        setImage(loaded);
        setStatus("ready");
      } catch (_) {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [source]);

  useLayoutEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;
    const measure = () => setStageSize({ width: node.clientWidth, height: node.clientHeight });
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const naturalWidth = image?.naturalWidth || 0;
  const naturalHeight = image?.naturalHeight || 0;
  const sideways = rotation % 180 !== 0;
  const frameWidth = sideways ? naturalHeight : naturalWidth;
  const frameHeight = sideways ? naturalWidth : naturalHeight;
  const fitScale = frameWidth && frameHeight
    ? Math.max(0, Math.min(
      (stageSize.width - STAGE_PADDING * 2) / frameWidth,
      (stageSize.height - STAGE_PADDING * 2) / frameHeight
    ))
    : 0;
  const displayWidth = Math.floor(frameWidth * fitScale);
  const displayHeight = Math.floor(frameHeight * fitScale);
  const aspectRatio = ASPECTS.find((item) => item.id === aspect)?.ratio || null;
  const normalizedRatio = aspectRatio && frameWidth && frameHeight ? aspectRatio * (frameHeight / frameWidth) : null;

  // Превью — уменьшенный повёрнутый кадр; яркость на нём — фильтром CSS, а в
  // готовый снимок она пишется по точкам (тот же множитель, что у фильтра).
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image || !displayWidth || !displayHeight) return;
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(displayWidth * ratio);
    canvas.height = Math.round(displayHeight * ratio);
    const context = canvas.getContext("2d");
    if (!context) return;
    const scale = (displayWidth * ratio) / frameWidth;
    context.setTransform(scale, 0, 0, scale, 0, 0);
    context.clearRect(0, 0, frameWidth, frameHeight);
    context.translate(frameWidth / 2, frameHeight / 2);
    context.rotate((rotation * Math.PI) / 180);
    context.drawImage(image, -naturalWidth / 2, -naturalHeight / 2);
  }, [image, rotation, displayWidth, displayHeight, frameWidth, frameHeight, naturalWidth, naturalHeight]);

  const rotate = (clockwise) => {
    const nextRotation = (rotation + (clockwise ? 90 : 270)) % 360;
    setRotation(nextRotation);
    if (aspectRatio) {
      // Кадр лёг боком — рамку того же соотношения строим заново по центру.
      const nextSideways = nextRotation % 180 !== 0;
      setCrop(centeredCrop(aspectRatio, nextSideways ? naturalHeight : naturalWidth, nextSideways ? naturalWidth : naturalHeight));
    } else {
      setCrop((current) => rotateCrop(current, clockwise));
    }
  };

  const chooseAspect = (id) => {
    setAspect(id);
    const ratio = ASPECTS.find((item) => item.id === id)?.ratio || null;
    if (ratio) setCrop(centeredCrop(ratio, frameWidth, frameHeight));
  };

  const startDrag = (event, mode) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    // Прежнее перетаскивание (второй палец) — снять до нового.
    stopDragRef.current?.();
    dragRef.current = { mode, startX: event.clientX, startY: event.clientY, start: crop, pointerId: event.pointerId };
    const minW = displayWidth ? Math.min(1, MIN_CROP_PX / displayWidth) : 0.1;
    const minH = displayHeight ? Math.min(1, MIN_CROP_PX / displayHeight) : 0.1;
    const onMove = (moveEvent) => {
      const drag = dragRef.current;
      if (!drag || moveEvent.pointerId !== drag.pointerId) return;
      moveEvent.preventDefault();
      const dx = (moveEvent.clientX - drag.startX) / (displayWidth || 1);
      const dy = (moveEvent.clientY - drag.startY) / (displayHeight || 1);
      setCrop(dragCrop(drag.mode, drag.start, dx, dy, normalizedRatio, minW, minH));
    };
    const stopDrag = () => {
      dragRef.current = null;
      stopDragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    const onUp = (upEvent) => {
      if (dragRef.current && upEvent.pointerId !== dragRef.current.pointerId) return;
      stopDrag();
    };
    stopDragRef.current = stopDrag;
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const changed = rotation !== 0 || brightness !== 0 || !sameCrop(crop, FULL_CROP);

  const reset = () => {
    setRotation(0);
    setAspect("free");
    setCrop({ ...FULL_CROP });
    setBrightness(0);
  };

  const exportEdited = async () => {
    const cropX = Math.round(crop.x * frameWidth);
    const cropY = Math.round(crop.y * frameHeight);
    const cropW = Math.max(1, Math.min(frameWidth - cropX, Math.round(crop.w * frameWidth)));
    const cropH = Math.max(1, Math.min(frameHeight - cropY, Math.round(crop.h * frameHeight)));
    const scale = cropW * cropH > MAX_OUTPUT_PIXELS ? Math.sqrt(MAX_OUTPUT_PIXELS / (cropW * cropH)) : 1;
    const outputWidth = Math.max(1, Math.round(cropW * scale));
    const outputHeight = Math.max(1, Math.round(cropH * scale));
    const canvas = document.createElement("canvas");
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("canvas-unavailable");
    // Белый фон — как при переводе PNG в JPEG при добавлении фото: прозрачное
    // иначе стало бы чёрным.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, outputWidth, outputHeight);
    context.scale(scale, scale);
    context.translate(-cropX, -cropY);
    context.translate(frameWidth / 2, frameHeight / 2);
    context.rotate((rotation * Math.PI) / 180);
    context.drawImage(image, -naturalWidth / 2, -naturalHeight / 2);
    if (brightness !== 0) {
      const factor = 1 + brightness / 100;
      const pixels = context.getImageData(0, 0, outputWidth, outputHeight);
      const data = pixels.data;
      // Uint8ClampedArray сам обрезает и округляет — как brightness() в CSS.
      for (let index = 0; index < data.length; index += 4) {
        data[index] *= factor;
        data[index + 1] *= factor;
        data[index + 2] *= factor;
      }
      context.putImageData(pixels, 0, 0);
    }
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) throw new Error("jpeg-export-failed");
    const baseName = String(fileName || "photo").replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  };

  const save = async () => {
    if (status !== "ready" || saving) return;
    if (!changed) {
      onCancel();
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const file = await exportEdited();
      onSave(file);
    } catch (_) {
      setSaveError(t("Не удалось сохранить фото. Попробуйте ещё раз."));
      setSaving(false);
    }
  };

  // События не уходят в окно публикации под редактором: портал передаёт их
  // по дереву React, и нажатие здесь начинало бы там перетаскивание плитки.
  const stop = (event) => event.stopPropagation();

  return createPortal((
    <div
      className="pe-overlay"
      onPointerDown={stop}
      onMouseDown={stop}
      onTouchStart={stop}
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget && !saving) onCancel();
      }}
      onKeyDown={stop}
    >
      <div className="pe-window" role="dialog" aria-modal="true" aria-label={t("Редактор фото")}>
        <div className="pe-head">
          <div className="pe-title">{t("Редактор фото")}</div>
          <button type="button" className="pe-icon-btn" onClick={onCancel} disabled={saving} title={t("Закрыть")} aria-label={t("Закрыть")}>
            <XIcon size={16} />
          </button>
        </div>

        <div className="pe-stage" ref={stageRef}>
          {status === "loading" && <div className="pe-status">{t("Загружаем фото…")}</div>}
          {status === "error" && <div className="pe-status is-error">{t("Не удалось открыть фото")}</div>}
          {status === "ready" && displayWidth > 0 && displayHeight > 0 && (
            <div className="pe-frame" style={{ width: `${displayWidth}px`, height: `${displayHeight}px` }}>
              <canvas
                ref={canvasRef}
                className="pe-canvas"
                style={{ width: `${displayWidth}px`, height: `${displayHeight}px`, filter: brightness ? `brightness(${1 + brightness / 100})` : "none" }}
              />
              <div
                className="pe-crop"
                style={{
                  left: `${crop.x * displayWidth}px`,
                  top: `${crop.y * displayHeight}px`,
                  width: `${crop.w * displayWidth}px`,
                  height: `${crop.h * displayHeight}px`
                }}
                onPointerDown={(event) => startDrag(event, "move")}
              >
                <span className="pe-grid" aria-hidden="true" />
                {["nw", "ne", "sw", "se"].map((corner) => (
                  <span
                    key={corner}
                    className={`pe-handle is-${corner}`}
                    onPointerDown={(event) => startDrag(event, corner)}
                    aria-hidden="true"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="pe-tools">
          <div className="pe-row">
            <div className="pe-group">
              <button type="button" className="pe-btn" onClick={() => rotate(false)} disabled={status !== "ready" || saving} title={t("Повернуть влево")} aria-label={t("Повернуть влево")}>
                <RotateLeftIcon size={16} />
              </button>
              <button type="button" className="pe-btn" onClick={() => rotate(true)} disabled={status !== "ready" || saving} title={t("Повернуть вправо")} aria-label={t("Повернуть вправо")}>
                <RotateRightIcon size={16} />
              </button>
            </div>
            <div className="pe-group pe-aspects" role="group" aria-label={t("Обрезка")}>
              <CropIcon size={15} className="pe-group-icon" />
              {ASPECTS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`pe-chip${aspect === item.id ? " is-on" : ""}`}
                  onClick={() => chooseAspect(item.id)}
                  disabled={status !== "ready" || saving}
                  aria-pressed={aspect === item.id}
                >
                  {item.id === "free" ? t("Свободно") : item.id}
                </button>
              ))}
            </div>
          </div>
          <label className="pe-slider">
            <BrightnessIcon size={16} className="pe-group-icon" />
            <span className="pe-slider-label">{t("Яркость")}</span>
            <input
              type="range"
              min={-50}
              max={50}
              step={1}
              value={brightness}
              onChange={(event) => setBrightness(Number(event.target.value) || 0)}
              onDoubleClick={() => setBrightness(0)}
              disabled={status !== "ready" || saving}
            />
            <span className="pe-value">{brightness > 0 ? `+${brightness}` : brightness}</span>
          </label>
          {saveError && <div className="pe-error">{saveError}</div>}
        </div>

        <div className="pe-foot">
          <button type="button" className="pe-btn is-ghost" onClick={reset} disabled={!changed || saving}>
            {t("Сбросить")}
          </button>
          <div className="pe-spacer" />
          <button type="button" className="pe-btn is-ghost" onClick={onCancel} disabled={saving}>
            {t("Отмена")}
          </button>
          <button type="button" className="pe-btn is-primary" onClick={save} disabled={status !== "ready" || saving}>
            {saving ? t("Сохраняем…") : t("Готово")}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};

export default PhotoEditor;
